import type { DefaultRootState } from "react-redux";
import type { Dispatch } from "redux";
import type { ThunkAction } from "redux-thunk";
import {
  type IFeatureFlagMerchantStatuses,
  getFeatureFlagStatus,
  getFeatureFlags,
} from "interfaces/super_users/feature_flags";
import {
  type IMerchantGroup,
  deleteStopMerchantGroupSession,
  getSearchMerchantGroups,
  postStartMerchantGroupSession,
} from "interfaces/super_users/merchant_group_sessions";

import { RequestStatus } from "types/request";

export type SuperUserActionTypes =
  | ISearchMerchantGroupsAction
  | ISetFeatureFlags
  | ISetFeatureFlagStatuses
  | ISetMerchantGroupsQueryAction
  | ISetSuperUserRequestStatusAction
  | IStartMerchantGroupSessionAction
  | IStopMerchantGroupSessionAction;

export type ThunkResult<R> = ThunkAction<
  R,
  DefaultRootState,
  undefined,
  SuperUserActionTypes
>;

export enum TypeKeys {
  SEARCH_MERCHANT_GROUPS = "SUPER_USER/SEARCH_MERCHANT_GROUPS",
  SET_FEATURE_FLAG_STATUSES = "SUPER_USER/SET_FEATURE_FLAG_STATUSES",
  SET_FEATURE_FLAGS = "SUPER_USER/SET_FEATURE_FLAGS",
  SET_MERCHANT_GROUPS_QUERY_ACTION = "SUPER_USER/SET_MERCHANT_GROUPS_QUERY_ACTION",
  SET_SUPER_USER_REQUEST_STATUS_ACTION = "SUPER_USER/SET_SUPER_USER_REQUEST_STATUS_ACTION",
  START_MERCHANT_GROUP_SESSION = "SUPER_USER/START_MERCHANT_GROUP_SESSION",
  STOP_MERCHANT_GROUP_SESSION = "SUPER_USER/STOP_MERCHANT_GROUP_SESSION",
}

export enum SuperUserRequestKey {
  GetFeatureFlags = "GetFeatureFlags",
  GetFeatureFlagStatuses = "GetFeatureFlagStatuses",
  SearchMerchantGroups = "SearchMerchantGroups",
  StartMerchantGroupsSession = "StartMerchantGroupsSession",
  StopMerchantGroupsSession = "StopMerchantGroupsSession",
}

export interface ISetSuperUserRequestStatusAction {
  type: TypeKeys.SET_SUPER_USER_REQUEST_STATUS_ACTION;
  requestKey: SuperUserRequestKey;
  requestStatus: RequestStatus;
}

export interface ISetMerchantGroupsQueryAction {
  type: TypeKeys.SET_MERCHANT_GROUPS_QUERY_ACTION;
  query: string;
}

export interface ISearchMerchantGroupsAction {
  type: TypeKeys.SEARCH_MERCHANT_GROUPS;
  merchantGroups: IMerchantGroup[];
}

export interface IStartMerchantGroupSessionAction {
  type: TypeKeys.START_MERCHANT_GROUP_SESSION;
  salesforceId: string;
}

export interface IStopMerchantGroupSessionAction {
  type: TypeKeys.STOP_MERCHANT_GROUP_SESSION;
}

const setSuperUserRequestStatus = (
  requestKey: SuperUserRequestKey,
  requestStatus: RequestStatus
): ISetSuperUserRequestStatusAction => {
  return {
    type: TypeKeys.SET_SUPER_USER_REQUEST_STATUS_ACTION,
    requestKey,
    requestStatus,
  };
};

export const setMerchantGroupsQuery = (query: string): ThunkResult<void> => {
  return (dispatch: Dispatch<any>): Promise<void> => {
    return new Promise(() => {
      dispatch({
        type: TypeKeys.SET_MERCHANT_GROUPS_QUERY_ACTION,
        query,
      });
    });
  };
};

export const searchMerchantGroups = (query: string): ThunkResult<void> => {
  return (dispatch: Dispatch<any>): Promise<void> => {
    dispatch(
      setSuperUserRequestStatus(
        SuperUserRequestKey.SearchMerchantGroups,
        RequestStatus.REQUESTING
      )
    );

    return getSearchMerchantGroups(query).then((merchantGroups) => {
      dispatch(
        setSuperUserRequestStatus(
          SuperUserRequestKey.SearchMerchantGroups,
          RequestStatus.SUCCESS
        )
      );
      dispatch({
        type: TypeKeys.SEARCH_MERCHANT_GROUPS,
        merchantGroups,
      });
    });
  };
};

export const SUPERUSER_LOCATION_ACCOUNT_KEY = "superuserLocationAccount";
export const startMerchantGroupSession = (
  salesforceId: string,
  accountName?: string
): ThunkResult<void> => {
  return (dispatch: Dispatch<any>): Promise<void> => {
    dispatch(
      setSuperUserRequestStatus(
        SuperUserRequestKey.StartMerchantGroupsSession,
        RequestStatus.REQUESTING
      )
    );

    if (accountName) {
      localStorage.setItem(SUPERUSER_LOCATION_ACCOUNT_KEY, accountName);
    }

    return postStartMerchantGroupSession(salesforceId).then(() => {
      dispatch(
        setSuperUserRequestStatus(
          SuperUserRequestKey.StartMerchantGroupsSession,
          RequestStatus.SUCCESS
        )
      );
      dispatch({
        type: TypeKeys.START_MERCHANT_GROUP_SESSION,
        salesforceId,
      });

      // Redirect to root so that we can show them the correct location.
      if (window.location.hash.length > 0) {
        window.location.hash = "/";
        window.location.reload();
      } else {
        window.location.href = "/";
      }
    });
  };
};

export const stopMerchantGroupSession = (): ThunkResult<void> => {
  return (dispatch: Dispatch<any>): Promise<void> => {
    dispatch(
      setSuperUserRequestStatus(
        SuperUserRequestKey.StopMerchantGroupsSession,
        RequestStatus.REQUESTING
      )
    );

    return deleteStopMerchantGroupSession().then(() => {
      dispatch(
        setSuperUserRequestStatus(
          SuperUserRequestKey.StopMerchantGroupsSession,
          RequestStatus.SUCCESS
        )
      );
      dispatch({ type: TypeKeys.STOP_MERCHANT_GROUP_SESSION });
    });
  };
};

export interface ISetFeatureFlags {
  type: TypeKeys.SET_FEATURE_FLAGS;
  featureFlagNames: string[];
}

function setFlagNames(featureFlagNames: string[]): ISetFeatureFlags {
  return {
    type: TypeKeys.SET_FEATURE_FLAGS,
    featureFlagNames,
  };
}

export const fetchFeatureFlags = (): ThunkResult<void> => {
  return (dispatch: Dispatch<any>): Promise<void> => {
    dispatch(
      setSuperUserRequestStatus(
        SuperUserRequestKey.GetFeatureFlags,
        RequestStatus.REQUESTING
      )
    );
    return getFeatureFlags()
      .then((flagNames) => {
        dispatch(setFlagNames(flagNames));
        dispatch(
          setSuperUserRequestStatus(
            SuperUserRequestKey.GetFeatureFlags,
            RequestStatus.SUCCESS
          )
        );
      })
      .catch(() => {
        dispatch(
          setSuperUserRequestStatus(
            SuperUserRequestKey.GetFeatureFlags,
            RequestStatus.FAILURE
          )
        );
      });
  };
};

export interface ISetFeatureFlagStatuses {
  type: TypeKeys.SET_FEATURE_FLAG_STATUSES;
  featureFlagStatuses: IFeatureFlagMerchantStatuses;
}

function setFlagStatuses(
  featureFlagStatuses: IFeatureFlagMerchantStatuses
): ISetFeatureFlagStatuses {
  return {
    type: TypeKeys.SET_FEATURE_FLAG_STATUSES,
    featureFlagStatuses,
  };
}

export const fetchFeatureFlagStatuses = (
  flagName: string
): ThunkResult<void> => {
  return (dispatch: Dispatch<any>): Promise<void> => {
    dispatch(
      setSuperUserRequestStatus(
        SuperUserRequestKey.GetFeatureFlagStatuses,
        RequestStatus.REQUESTING
      )
    );
    return getFeatureFlagStatus(flagName)
      .then((flagStatuses) => {
        dispatch(setFlagStatuses(flagStatuses));
        dispatch(
          setSuperUserRequestStatus(
            SuperUserRequestKey.GetFeatureFlagStatuses,
            RequestStatus.SUCCESS
          )
        );
      })
      .catch(() => {
        dispatch(
          setSuperUserRequestStatus(
            SuperUserRequestKey.GetFeatureFlagStatuses,
            RequestStatus.FAILURE
          )
        );
      });
  };
};
